/* eslint-disable react/no-unknown-property */
/** @jsxImportSource @emotion/react */
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@emotion/react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Button from '@Button';
import Text from '@Text';
import { confirmClientApiStillPresentAction } from '../../Hooks/actionCreators';
import { exposeEventToPartner } from '../../Helpers/events';
import { useWidgetHeightRefresh } from '../../Hooks/useWidgetHeightRefresh/useWidgetHeightRefresh';
import useClientApiCopy from './copy/useClientApiCopy';
import makeInactivityPromptStyles, {
  getCountdownColour,
} from './makeInactivityPromptStyles';

/**
 * Asks a seemingly-absent user to confirm they are still on the journey.
 *
 * MUI's Modal directly rather than `@Modal`, which always renders a close X and
 * dismisses on backdrop and escape. Only the button may dismiss this one, so
 * `onClose` is omitted entirely: that removes all three in one stroke.
 */
const InactivityPrompt = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = makeInactivityPromptStyles({ theme });
  const copy = useClientApiCopy();
  const modalContainerRef = useRef(null);

  const isInactivityPromptOpen = useSelector(
    (store) => store.clientApiJourney?.isInactivityPromptOpen
  );
  const inactivityCountdownSeconds = useSelector(
    (store) => store.clientApiJourney?.inactivityCountdownSeconds
  );

  const iFrameId = useSelector((store) => store.optionalFormConfig?.iFrameId);

  useWidgetHeightRefresh([isInactivityPromptOpen]);

  useEffect(() => {
    if (!isInactivityPromptOpen) return;

    exposeEventToPartner({
      eventType: 'invokeAutoScroll',
      eventObject: { iFrameId },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInactivityPromptOpen]);

  const getParentElement = () => modalContainerRef.current?.parentElement;

  return (
    <div ref={modalContainerRef}>
      <Modal
        open={Boolean(isInactivityPromptOpen)}
        container={getParentElement}
        style={{ position: 'absolute' }}
        BackdropProps={{ style: { position: 'absolute' } }}
      >
        <Box css={classes.box} data-testid="inactivityPrompt">
          {/* Keyed on the seconds so each tick remounts it: the pulse is a CSS
              animation, which only replays on a new element. */}
          <Box
            key={inactivityCountdownSeconds}
            css={classes.countdown}
            style={{ color: getCountdownColour(inactivityCountdownSeconds) }}
            data-testid="inactivityCountdown"
          >
            {inactivityCountdownSeconds}
          </Box>
          <Box css={classes.heading}>
            <Text variant="question">{copy.inactivity.heading}</Text>
          </Box>
          <Box css={classes.subheading}>
            <Text variant="text">{copy.inactivity.subheading}</Text>
          </Box>
          <Box css={classes.confirmButton}>
            <Button
              label={copy.inactivity.confirm}
              onClick={() => dispatch(confirmClientApiStillPresentAction())}
              dataTestId="inactivityConfirm"
            />
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default InactivityPrompt;
